import { FC } from 'react';
import { Link, useHistory } from 'react-router-dom';

import {
    Banner,
    BodyText,
    Button,
    ButtonGroup,
    Eyebrow,
    Headline,
    Layout,
    Link as AnvilLink,
    Page,
    Stack,
} from '@servicetitan/design-system';

import { CopyBox, INTEGRATIONS_TEAM_EMAIL } from '../../common';
import { Paths } from '../../paths';
import { Steps } from '../step';
import { RequestAccessHeader } from './request-access-header';

interface CustomerStepProps {
    onBack: () => void;
}

export const CustomerStep: FC<CustomerStepProps> = ({ onBack }) => {
    const history = useHistory();

    return (
        <Page
            header={<RequestAccessHeader currentStep={Steps.GetAccess} />}
            footer={
                <Stack className="w-100" justifyContent="flex-end">
                    <ButtonGroup>
                        <Button onClick={onBack}>Back</Button>
                        <Button primary onClick={() => history.push('/')}>
                            Done
                        </Button>
                    </ButtonGroup>
                </Stack>
            }
        >
            <Stack direction="column" spacing={3}>
                <Stack direction="column" spacing={1}>
                    <Headline el="h2" size="medium">
                        Contact Our Integrations Team
                    </Headline>
                    <BodyText>
                        As a ServiceTitan customer building an internal app, please send an email
                        with the following information:
                    </BodyText>
                </Stack>

                <Banner status="warning" icon title="Security Reminder">
                    <BodyText>
                        Exercise caution when managing access to your Integration or Production
                        environments. Do not assign API permissions to third-party users outside
                        your organization. For details around approved integration paths, please
                        visit the{' '}
                        <Link className="Link Link--blue" to={Paths.customerIntegrationPaths}>
                            Customer Integration Paths page
                        </Link>
                        . If you have any further questions, contact{' '}
                        <AnvilLink primary href={`mailto:${INTEGRATIONS_TEAM_EMAIL}`}>
                            {INTEGRATIONS_TEAM_EMAIL}
                        </AnvilLink>{' '}
                        for guidance.
                    </BodyText>
                </Banner>

                <Layout type="2Col" spacing="compact">
                    <Layout.Section>
                        <Stack direction="column" spacing={1}>
                            <Eyebrow>Send Your Request To</Eyebrow>
                            <CopyBox size={30} value={INTEGRATIONS_TEAM_EMAIL} />
                        </Stack>
                    </Layout.Section>
                    <Layout.Section>
                        <Stack direction="column" spacing={1}>
                            <Eyebrow>What to Include in Your Email</Eyebrow>
                            <ul className="m-y-0">
                                <li>
                                    <BodyText>
                                        Your organization name and customer verification PIN
                                    </BodyText>
                                </li>
                                <li>
                                    <BodyText>
                                        Description of the app you want to build for your
                                        organization
                                    </BodyText>
                                </li>
                                <li>
                                    <BodyText>The scope and purpose of the application</BodyText>
                                </li>
                                <li>
                                    <BodyText>Your expected timeline for development</BodyText>
                                </li>
                            </ul>
                        </Stack>
                    </Layout.Section>
                </Layout>
            </Stack>
        </Page>
    );
};
CustomerStep.displayName = 'CustomerStep';
